import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import axios from 'axios';
import {
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
} from "@/layout/AppComponents";
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { axiosPath } from '../../../../routing/helpers/router';
import Translator from '../../../../layout/components/helpers/Translator';
import BreadcrumbHandler from '../../../../layout/components/breadcrumb/BreadcrumbHandler';
import AppSubmitButton from '../../../../layout/components/form/AppSubmitButton';
import errorHandler from '@/layout/components/error/ErrorHandler';
import * as currentPageAction from '../../../../store/actions/currentPageAction';
import ModuleComsaEnvironmentalSideBarReports from '../ModuleComsaEnvironmentalSideBarReports';
import ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard from '../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalConstruction/ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard';
import ModuleComsaEnvironmentalConstructionUseNormalAspectsCard from '../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalConstruction/ModuleComsaEnvironmentalConstructionUseNormalAspectsCard';
import ModuleComsaEnvironmentalTitleCard from '../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalTitleCard';
import useIsMounted from '../../../../layout/components/hooks/useIsMounted';

export default function ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsScreen() {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const [initialValues, setInitialValues] = useState();
  const [filterApplicable, setFilterApplicable] = useState(false);

  // > LOAD DATA //
  const loadData = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_get"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            sheet: "constructionDeconstructionNormalAspects",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            setInitialValues(r.data?.sheetData ?? {});
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  useEffect(() => {
    loadData();
  }, []);
  // < LOAD DATA //

  // > SAVE FORM //
  const handleSave = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_edit"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            payload,
            sheet: "constructionDeconstructionNormalAspects",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("moduleData", r.data?.moduleData, true)
            );
            AppNotificationAlert({
              message: ReactDOMServer.renderToString(
                <Translator file="module-comsaEnvironmental" name="completed" />
              ),
              type: "success",
              duration: 3000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );
  // < SAVE FORM //

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_module_comsaEnvironmental_getModuleData"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change("moduleData", r.data));
        }
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="construction"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <section className="content">
        <ModuleComsaEnvironmentalSideBarReports activeRoute="user_module_comsaEnvironmental_construction_deconstruction_normal_aspects" />
        <div className="row">
          <div className="col-12">
            <Title className="mb-4">
              <Translator
                file="module-legalCompliance"
                name="deconstruction.normals"
              />
            </Title>
          </div>
        </div>
        {initialValues && (
          <Formik
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSave}
          >
            {({
              setFieldValue,
              handleSubmit,
              values,
              errors,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <FilterApplicable
                  className="mt-2 mb-2"
                  title={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="filter.applicable"
                    />
                  }
                  onClick={() => setFilterApplicable(!filterApplicable)}
                  $filtered={filterApplicable}
                >
                  <AppRemixIcon icon="filter" className="mr-2" size={16} />
                  {filterApplicable ? (
                    <Translator
                      file="module-comsaEnvironmental"
                      name="remove.applicable.filter"
                    />
                  ) : (
                    <Translator
                      file="module-comsaEnvironmental"
                      name="filter.applicable"
                    />
                  )}
                </FilterApplicable>
                <h5 className="mt-2 mb-4">
                  <Translator
                    file="module-comsaEnvironmental"
                    name="life.cycle.stage.use.operation.maintenance.conservation"
                  />
                </h5>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="demolitions"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[0]}
                  setFieldValue={(n, v) => setFieldValue(`use.0.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="cleaning"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[1]}
                  setFieldValue={(n, v) => setFieldValue(`use.1.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="warning">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="emissions.combustion.gas.emission"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      }
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="air.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="traffic.circulation.infrastructure.users"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.use?.[2]}
                  setFieldValue={(n, v) => setFieldValue(`use.2.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="traffic.vehicles.machinery"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[3]}
                  setFieldValue={(n, v) => setFieldValue(`use.3.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      }
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="noise.vibrations.noise"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="noise.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.infrastructure.by.users"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.use?.[4]}
                  setFieldValue={(n, v) => setFieldValue(`use.4.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="demolitions"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[5]}
                  setFieldValue={(n, v) => setFieldValue(`use.5.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="traffic.vehicles.machinery"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[6]}
                  setFieldValue={(n, v) => setFieldValue(`use.6.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="noise.vibrations.vibrations"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="vibration.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.infrastructure.by.users"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.use?.[7]}
                  setFieldValue={(n, v) => setFieldValue(`use.7.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="aggregates"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[8]}
                  setFieldValue={(n, v) => setFieldValue(`use.8.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="discharges.water.discharges"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="water.soil.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.infrastructure.by.users"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.use?.[9]}
                  setFieldValue={(n, v) => setFieldValue(`use.9.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="sanitary.waters"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[10]}
                  setFieldValue={(n, v) => setFieldValue(`use.10.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="machinery"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[11]}
                  setFieldValue={(n, v) => setFieldValue(`use.11.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="cleaning"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[12]}
                  setFieldValue={(n, v) => setFieldValue(`use.12.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="watercourses.affections.watercourses"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="water.pollution.resource.depletion"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.infrastructure.by.users"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.use?.[13]}
                  setFieldValue={(n, v) => setFieldValue(`use.13.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="temporary.intakes"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[14]}
                  setFieldValue={(n, v) => setFieldValue(`use.14.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="marine.coastal.environment.alteration.water.seabed.characteristics"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="affects.water.pollution.ecosystem.alteration.species.disappearance"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.infrastructure.by.users"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.use?.[15]}
                  setFieldValue={(n, v) => setFieldValue(`use.15.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="conservation.tasks.machinery.circulation.sea"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[16]}
                  setFieldValue={(n, v) => setFieldValue(`use.16.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="natural.resources.consumption.raw.materials"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="depletion.resources"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="spare.parts"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[17]}
                  setFieldValue={(n, v) => setFieldValue(`use.17.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="natural.resources.water.consumption"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="depletion.resources"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="cleaning.watering"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[18]}
                  setFieldValue={(n, v) => setFieldValue(`use.18.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="natural.resources.fuel.consumption"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="depletion.resources"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.infrastructure.by.users"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.use?.[19]}
                  setFieldValue={(n, v) => setFieldValue(`use.19.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="vehicles.machinery"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[20]}
                  setFieldValue={(n, v) => setFieldValue(`use.20.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="natural.resources.bar.electricity.consumption"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="depletion.resources"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="infrastructure.lighting"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.use?.[21]}
                  setFieldValue={(n, v) => setFieldValue(`use.21.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="electrical.appliances"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[22]}
                  setFieldValue={(n, v) => setFieldValue(`use.22.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="waste.bar.urban.waste"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="visual.impact.land.occupation.water.pollution.soil.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="discharges.from.users.infrastructure"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.use?.[23]}
                  setFieldValue={(n, v) => setFieldValue(`use.23.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="assimilable.urban.solid.waste"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[24]}
                  setFieldValue={(n, v) => setFieldValue(`use.24.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="waste.bar.inert.waste"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="visual.impact.land.occupation.water.pollution.soil.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="discharges.from.users.infrastructure"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.use?.[25]}
                  setFieldValue={(n, v) => setFieldValue(`use.25.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="pruning.remains"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[26]}
                  setFieldValue={(n, v) => setFieldValue(`use.26.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="waste.bar.non.hazardous.waste"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="visual.impact.land.occupation.water.pollution.soil.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="discharges.from.users.infrastructure"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.use?.[27]}
                  setFieldValue={(n, v) => setFieldValue(`use.27.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="packaging.packaging.materials"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[28]}
                  setFieldValue={(n, v) => setFieldValue(`use.28.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="waste.bar.hazardous.waste"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="operation"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="discharges.from.users.infrastructure"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.use?.[29]}
                  setFieldValue={(n, v) => setFieldValue(`use.29.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="oils.containers.machinery.filters"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[30]}
                  setFieldValue={(n, v) => setFieldValue(`use.30.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="signaling.piles.beacons"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[31]}
                  setFieldValue={(n, v) => setFieldValue(`use.31.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="signaling.paint.container"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[32]}
                  setFieldValue={(n, v) => setFieldValue(`use.32.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="fuel.container"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[33]}
                  setFieldValue={(n, v) => setFieldValue(`use.33.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="socioeconomic.impact.bar.economic.activity"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="increase.decrease.economic.activity"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="increase.decrease.economic.activity"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.use?.[34]}
                  setFieldValue={(n, v) => setFieldValue(`use.34.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="socioeconomic.impact.bar.economic.activity"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="job.creation"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="job.creation"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation.maintenance.conservation"
                    />
                  }
                  values={values?.use?.[35]}
                  setFieldValue={(n, v) => setFieldValue(`use.35.${n}`, v)}
                />
                <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="socioeconomic.impact.bar.effects.on.mobility"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="effects.mobility"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="traffic.or.pedestrian.detours.diversions"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="operation"
                    />
                  }
                  values={values?.use?.[36]}
                  setFieldValue={(n, v) => setFieldValue(`use.36.${n}`, v)}
                />
                <ModuleComsaEnvironmentalConstructionUseNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="traffic.or.pedestrian.detours.diversions"
                    />
                  }
                  subphase={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="maintenance.conservation"
                    />
                  }
                  values={values?.use?.[37]}
                  setFieldValue={(n, v) => setFieldValue(`use.37.${n}`, v)}
                />
                <h5 className="mt-4 mb-4">
                  <Translator
                    file="module-comsaEnvironmental"
                    name="life.cycle.stage.deconstruction.dismantling"
                  />
                </h5>
                <ModuleComsaEnvironmentalTitleCard color="warning">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="emissions.particle.emissions"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="air.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="demolitions"
                    />
                  }
                  values={values?.deconstruction?.[0]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.0.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="blastings"
                    />
                  }
                  values={values?.deconstruction?.[1]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.1.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="machinery.circulation"
                    />
                  }
                  values={values?.deconstruction?.[2]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.2.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="transportation.soil.debris"
                    />
                  }
                  values={values?.deconstruction?.[3]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.3.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="warning">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="emissions.combustion.gas.emission"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="air.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="crushing.aggregates"
                    />
                  }
                  values={values?.deconstruction?.[4]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.4.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="machinery.circulation"
                    />
                  }
                  values={values?.deconstruction?.[5]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.5.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="generator.sets"
                    />
                  }
                  values={values?.deconstruction?.[6]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.6.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="noise.vibrations.noise"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="noise.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="machinery.circulation"
                    />
                  }
                  values={values?.deconstruction?.[7]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.7.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="demolitions"
                    />
                  }
                  values={values?.deconstruction?.[8]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.8.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="crushing.aggregates"
                    />
                  }
                  values={values?.deconstruction?.[9]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.9.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="blastings"
                    />
                  }
                  values={values?.deconstruction?.[10]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.10.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="noise.vibrations.vibrations"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="vibration.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="blastings"
                    />
                  }
                  values={values?.deconstruction?.[11]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.11.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="heavy.machinery.circulation"
                    />
                  }
                  values={values?.deconstruction?.[12]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.12.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="discharges.water.discharges"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="water.soil.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="sanitary.waters"
                    />
                  }
                  values={values?.deconstruction?.[13]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.13.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="machinery"
                    />
                  }
                  values={values?.deconstruction?.[14]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.14.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="cleaning"
                    />
                  }
                  values={values?.deconstruction?.[15]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.15.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="watercourses.affections.watercourses"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="water.pollution.resource.depletion"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="deconstruction.bridges.viaducts"
                    />
                  }
                  values={values?.deconstruction?.[16]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.16.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="temporary.intakes"
                    />
                  }
                  values={values?.deconstruction?.[17]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.17.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="warning">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="morphology.effects.soil.subsoil"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="soil.subsoil.alteration"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="blastings"
                    />
                  }
                  values={values?.deconstruction?.[18]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.18.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="storage.aggregates.waste"
                    />
                  }
                  values={values?.deconstruction?.[19]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.19.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="marine.coastal.environment.bar.alteration.water.seabed.characteristics"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="affects.water.pollution.ecosystem.alteration.species.disappearance"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="underwater.blasting"
                    />
                  }
                  values={values?.deconstruction?.[20]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.20.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="machinery.circulation"
                    />
                  }
                  values={values?.deconstruction?.[21]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.21.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="storage.aggregates.areas.sea"
                    />
                  }
                  values={values?.deconstruction?.[22]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.22.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="danger">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="impacts.fauna.disturbance.wildlife"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="alteration.ecosystems.disappearance.animal.species"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.infrastructure.by.users"
                    />
                  }
                  values={values?.deconstruction?.[23]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.23.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="traffic.vehicles.machinery"
                    />
                  }
                  values={values?.deconstruction?.[24]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.24.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="impact.vegetation.damage.removal.vegetation"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="alteration.ecosystems.disappearance.plant.species"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="felling.clearing"
                    />
                  }
                  values={values?.deconstruction?.[25]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.25.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="storage.aggregates.waste"
                    />
                  }
                  values={values?.deconstruction?.[26]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.26.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="machinery.circulation"
                    />
                  }
                  values={values?.deconstruction?.[27]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.27.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="danger">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      {
                        <Translator
                          file="module-comsaEnvironmental"
                          name="affectation.fauna.bar.disturbance.wildlife"
                        />
                      }
                      <Translator
                        file="module-comsaEnvironmental"
                        name="affectation.fauna.bar.disturbance.wildlife"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="alteration.ecosystems.disappearance.animal.species"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="machinery.circulation"
                    />
                  }
                  values={values?.deconstruction?.[28]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.28.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="felling.clearing"
                    />
                  }
                  values={values?.deconstruction?.[29]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.29.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="natural.resources.bar.fuel.consumption"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="depletion.resources"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="use.machinery"
                    />
                  }
                  values={values?.deconstruction?.[30]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.30.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="waste.bar.urban.waste"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="visual.impact.land.occupation.water.pollution.soil.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="assimilable.urban.solid.waste"
                    />
                  }
                  values={values?.deconstruction?.[31]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.31.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="waste.bar.inert.waste"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="visual.impact.land.occupation.water.pollution.soil.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="earth.debris.from.demolition"
                    />
                  }
                  values={values?.deconstruction?.[32]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.32.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="plant.pruning.residues"
                    />
                  }
                  values={values?.deconstruction?.[33]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.33.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="concrete.sleepers"
                    />
                  }
                  values={values?.deconstruction?.[34]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.34.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="ballast"
                    />
                  }
                  values={values?.deconstruction?.[35]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.35.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="waste.bar.non.hazardous.waste"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="visual.impact.land.occupation.water.pollution.soil.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="paper.cardboard"
                    />
                  }
                  values={values?.deconstruction?.[36]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.36.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="plastics"
                    />
                  }
                  values={values?.deconstruction?.[37]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.37.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator file="module-comsaEnvironmental" name="wood" />
                  }
                  values={values?.deconstruction?.[38]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.38.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="wooden.sleepers"
                    />
                  }
                  values={values?.deconstruction?.[39]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.39.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="iron.scrap.metal.from.demolitions.dismantling"
                    />
                  }
                  values={values?.deconstruction?.[40]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.40.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="waste.bar.hazardous.waste"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="visual.impact.land.occupation.water.pollution.soil.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="oils.containers.machinery.filters"
                    />
                  }
                  values={values?.deconstruction?.[41]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.41.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="oils.transformation.centers"
                    />
                  }
                  values={values?.deconstruction?.[42]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.42.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="asbestos.fibrocement"
                    />
                  }
                  values={values?.deconstruction?.[43]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.43.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="machinery.batteries"
                    />
                  }
                  values={values?.deconstruction?.[44]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.44.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="signaling.marking.batteries"
                    />
                  }
                  values={values?.deconstruction?.[45]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.45.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="fuel.containers"
                    />
                  }
                  values={values?.deconstruction?.[46]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.46.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="rags.gloves.etc.with.oils"
                    />
                  }
                  values={values?.deconstruction?.[47]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.47.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="order.bar.cleanliness"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="visual.impact.soil.alteration"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="interior.cleaning"
                    />
                  }
                  values={values?.deconstruction?.[48]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.48.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="exterior.cleaning"
                    />
                  }
                  values={values?.deconstruction?.[49]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.49.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="primary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="heritage.bar.historical.artistic"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="impact.on.heritage"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="earthworks"
                    />
                  }
                  values={values?.deconstruction?.[50]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.50.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="socioeconomic.impact.bar.effects.on.mobility"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="impact.mobility"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="road.traffic.closures.diversions"
                    />
                  }
                  values={values?.deconstruction?.[51]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.51.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="pedestrian.closures.diversions"
                    />
                  }
                  values={values?.deconstruction?.[52]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.52.${n}`, v)
                  }
                />
                <ModuleComsaEnvironmentalConstructionDeconstructionNormalAspectsCard
                  filterApplicable={filterApplicable}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="rail.traffic.closures.alterations"
                    />
                  }
                  values={values?.deconstruction?.[53]}
                  setFieldValue={(n, v) =>
                    setFieldValue(`deconstruction.53.${n}`, v)
                  }
                />
                <SubmitWrapper>
                  <AppSubmitButton
                    className="btn btn-sm btn-success"
                    isSubmitting={isSubmitting}
                  >
                    <Translator file="module-comsaEnvironmental" name="save" />
                  </AppSubmitButton>
                </SubmitWrapper>
                <Footer className="mt-5 mb-5">
                  <Translator
                    file="module-comsaEnvironmental"
                    name="best.practices.included.maintenance.manual.built.project"
                  />
                </Footer>
              </form>
            )}
          </Formik>
        )}
      </section>
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;

const Footer = styled.h3`
  text-align: center;
  font-size: 18px;
`;

const SubmitWrapper = styled.div`
  position: fixed;
  right: 15px;
  bottom: 8px;
  z-index: 10;
`;

const FilterApplicable = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  background-color: #28a745;
  color: #fff;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 3px;
  border: 1px solid #28a745;
  color: ${(props) => (props.$filtered ? '#28a745' : '#fff')};
  background-color: ${(props) => (props.$filtered ? '#fff' : '#28a745')};
`;
